import { useEffect, useState } from "react";
import axios from "axios";
import {
    Card,
    CardContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Typography,
    TableContainer,
    Paper,
    Tooltip,
    Slide,
    Fade,
    Chip,
    TablePagination,
    Box,
} from "@mui/material";
import { format } from "date-fns";
import baseURL from "../../config";
import Navbar from "../../Navbar/Navbar";
import { Token } from "@mui/icons-material";

const ExpiringShipDetails = () => {
    const [ships, setShips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); 
    setPage(0);
  };

  const currentPageData = ships.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        const fetchExpiringShips = async () => {
            const token = localStorage.getItem("token");
            try {
                const response = await axios.get(`${baseURL}/api/getExpiringShipDetails`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setShips(response.data.data);
                setTimeout(() => setShowTable(true), 500);
            } catch (err) {
                console.error("Error fetching ships:", err);
                setError(err?.response?.data?.message || "Failed to fetch ship details");
            } finally {
                setLoading(false);
            }
        };
        fetchExpiringShips();
    }, []);

    const getStatus = (date) => {
        if (!date) return { text: "N/A", tooltip: "No data available", component: "N/A" };
    
        const expiryDate = new Date(date);
        const today = new Date();
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        const formattedDate = format(expiryDate, "dd-MM-yyyy");

        if (expiryDate < today) {
            return {
                text: formattedDate,
                tooltip: "🚨 Expired! Immediate attention needed.",
                component: (
                    <Tooltip title="🚨 Expired! Immediate action required." arrow>
                        <Chip
                            label={`❌ Expired (${formattedDate})`}
                            sx={{
                                background: "linear-gradient(135deg, #ff0000, #d50000)",
                                color: "white",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                boxShadow: "0 0 20px rgba(255, 0, 0, 0.9)",
                                animation: "blink 1.5s infinite",
                                "@keyframes blink": {
                                    "0%": { boxShadow: "0 0 15px rgba(255, 0, 0, 0.8)" },
                                    "50%": { boxShadow: "0 0 30px rgba(255, 0, 0, 1)" },
                                    "100%": { boxShadow: "0 0 15px rgba(255, 0, 0, 0.8)" },
                                },
                            }}
                        />
                    </Tooltip>
                ),
            };
        } else if (daysLeft <= 30) {
            return {
                text: formattedDate,
                tooltip: `⚠️ Expiring soon in ${daysLeft} days!`,
                component: (
                    <Tooltip title={`⚠️ Expiring in ${daysLeft} days!`} arrow>
                        <Chip
                            label={`⚠️ ${formattedDate} (${daysLeft} days left)`}
                            sx={{
                                background: "linear-gradient(135deg, #ff9800, #ff5722)",
                                color: "white",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                boxShadow: "0 0 20px rgba(255, 140, 0, 0.9)",
                                animation: "pulse 1.5s infinite alternate",
                                "@keyframes pulse": {
                                    "0%": { boxShadow: "0 0 15px rgba(255, 140, 0, 0.7)" },
                                    "100%": { boxShadow: "0 0 25px rgba(255, 140, 0, 1)" },
                                },
                            }}
                        />
                    </Tooltip>
                ),
            };
        } else {
            return {
                text: formattedDate,
                tooltip: "✅ Valid and up to date",
                component: (
                    <Tooltip title="✅ This document is valid" arrow>
                        <Chip
                            label={`✅ ${formattedDate}`}
                            sx={{
                                background: "linear-gradient(135deg, #00c853, #2E7D32)",
                                color: "white",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                boxShadow: "0 0 20px rgba(0, 255, 0, 0.8)",
                                animation: "softGlow 2s infinite alternate",
                                "@keyframes softGlow": {
                                    "0%": { boxShadow: "0 0 10px rgba(0, 255, 0, 0.6)" },
                                    "100%": { boxShadow: "0 0 20px rgba(0, 255, 0, 1)" },
                                },
                            }}
                        />
                    </Tooltip>
                ),
            };
        }
    };
    
    return (
        <>
            <Navbar />

            {/* Error message */}
                {error && (
                  <Box p={4} textAlign="center" bgcolor="#fdecea" color="#b71c1c">
                    <Typography color="error" variant="h6">
                      {error}
                    </Typography>
                  </Box>
                )}
            
            
            <Slide direction="up" in={true} timeout={600}>
                <Card
                    sx={{
                        p: 4,
                        boxShadow: "0 4px 25px rgba(0, 0, 0, 0.3)",
                        mt: 2,
                        borderRadius: 3,
                        background: "#b8860b",
                    }}
                >
                    <CardContent>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            align="center"
                            mb={2}
                            sx={{
                                color: "white",
                                textShadow: "2px 2px 15px rgba(0, 0, 0, 0.3)",
                                background: "#C30B12",
                                p: 2,
                                borderRadius: "12px",
                                boxShadow: "0 0 20px rgba(255, 255, 255, 0.2)",
                            }}
                        >
                            🚢 Expiring Vessels (In Next 30 Days)
                        </Typography>

                        {loading ? (
                            <CircularProgress sx={{ display: "block", mx: "auto", my: 2 }} />
                        ) : (
                            <Fade in={showTable} timeout={800}>
                                <TableContainer component={Paper} sx={{  overflow: "hidden", borderRadius: 2, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)" }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "#0d47a1" }}>
                                            <TableCell sx={{ color: "black", fontWeight: "bold" }}>Sr. No.</TableCell>
                                                <TableCell sx={{ color: "black", fontWeight: "bold" }}>Vessel Name</TableCell>
                                                <TableCell sx={{ color: "black", fontWeight: "bold" }}>Company</TableCell>
                                                <TableCell sx={{ color: "black", fontWeight: "bold" }}>MLC Expiry</TableCell>
                                                <TableCell sx={{ color: "black", fontWeight: "bold" }}>Policy Expiry</TableCell>
                                                <TableCell sx={{ color: "black", fontWeight: "bold" }}>Financial Security Validity</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {currentPageData.map((ship, index) => (
                                                <Slide direction="left" in={true} timeout={500 + index * 200} key={ship._id}>
                                                    <TableRow
                                                        hover
                                                        sx={{
                                                            transition: "background 0.4s ease-in-out",
                                                            backgroundColor: ship.mlcExpiryDate && new Date(ship.mlcExpiryDate) < new Date() ? "#ffebee" : "inherit",
                                                            "&:hover": { backgroundColor: "#e1f5fe" },
                                                        }}
                                                    >
                                                         <TableCell sx={{ fontWeight: "bold" }}>{page * rowsPerPage + index + 1 || "N/A"}</TableCell>
                                                        <TableCell sx={{ fontWeight: "bold" }}>{ship.vesselname || "N/A"}</TableCell>
                                                        <TableCell sx={{ fontWeight: "bold" }}>{ship.company_name?.company_shortname || "N/A"}</TableCell>
                                                        {["mlcExpiryDate", "policydateofv", "fsdvalidity"].map((field) => {
                                                            const { component } = getStatus(ship[field]);
                                                            return <TableCell key={field}>{component}</TableCell>;
                                                        })}
                                                    </TableRow>
                                                </Slide>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
        component="div"
        count={ships.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
      />
                                </TableContainer>
                            </Fade>
                        )}
                    </CardContent>
                </Card>
            </Slide>
        </>
    );
};

export default ExpiringShipDetails;
